import DefaultPending from '@/components/DefaultPending'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { CashDrawerMenu } from '@/components/pos/CashDrawerMenu'
import { LocationSelector } from '@/components/pos/LocationSelector'
import { POSCart } from '@/components/pos/POSCart'
import { PosWorkspaceLayout } from '@/components/pos/PosWorkspaceLayout'
import { PosCartLockBanner } from '@/components/pos/PosCartLockBanner'
import { ProductCatalog } from '@/components/pos/ProductCatalog'
import { RecoverablePosCartBanner } from '@/components/pos/RecoverablePosCartBanner'
import { SavedPosCartsLink } from '@/components/pos/SavedPosCartsLink'
import { SetupRequirementsCards } from '@/components/SetupRequirementsCards'
import { CartSource } from '@/lib/enums/CartSource'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { ensurePosSetupRequirements, usePosSetupRequirements } from '@/lib/pos/setup-requirements'
import { createPosCartActions, usePosCartWithPendingAdds } from '@/lib/pos/cart-actions'
import { getPosCartLeaveAction, shouldAskBeforeLeavingCart } from '@/lib/pos/cart-leave'
import {
  findLastPosCartToReopen,
  forgetLastPosCart,
  rememberLastPosCart,
} from '@/lib/pos/last-pos-cart'
import { resolvePosFulfillmentLocation } from '@/lib/pos/fulfillment-availability'
import {
  cartQueries,
  useCreateCartMutation,
  useIsCartMutating,
  useIsCartOrderEnding,
  usePosCart,
} from '@/lib/services/carts/carts.query'
import { categoryQueries } from '@/lib/services/categories/categories.query'
import { salesOrderCreateSearchSchema } from '@/lib/services/sales-orders/sales-order.schema'
import { getErrorMessage } from '@/lib/utils'
import { loadLocalPrintProfile } from '@/lib/local-print'
import queryClient from '@/lib/queryClient'
import {
  createFileRoute,
  notFound,
  redirect,
  useBlocker,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import { usePrevious } from '@uidotdev/usehooks'
import { ShoppingCart } from 'lucide-react'
import { useEffect, useEffectEvent, useState } from 'react'
import { toast } from 'sonner'

const posSetupLocationFilters = {
  include: ['address', 'availabilityChanges', 'deliveryPostalCodeFees', 'deliveryAreaFees'],
  active: true,
}

function CreateSalesOrderPageHeader({ actions }) {
  return (
    <AdminPageHeader icon={ShoppingCart} title="Create Order" density="compact" actions={actions} />
  )
}

const UNSAVED_PAID_CART_LEAVE_MESSAGE =
  "This order has a payment and isn't saved as a draft. Save it as a draft so your team can find it in Draft Orders. Leave anyway?"

function cloneCartSnapshot(cart) {
  if (!cart) {
    return null
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(cart)
  }

  return JSON.parse(JSON.stringify(cart))
}

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/create')({
  component: CreateSalesOrder,
  validateSearch: (search) => salesOrderCreateSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ cartId: search?.cartId }),
  loader: async ({ context, deps, params, preload }) => {
    if (!context.ability.can('create', 'sales-orders')) throw notFound()

    // Create Order opened without a cart (the + button, Create Order on Orders) reopens the cart this computer was
    // working on, so leaving the POS mid-order never loses it. Starting from a customer or copying an order arrives
    // with its own cart. Hovering decides too: the router shows what a hover loaded the moment the link is clicked, so
    // an undecided hover would flash an empty cart before the reopened one.
    if (!deps?.cartId) {
      const lastCartId = await findLastPosCartToReopen(context.queryClient, params.orgId)
      if (lastCartId) {
        throw redirect({
          to: '/$orgId/sales-orders/create',
          params: { orgId: params.orgId },
          search: { cartId: lastCartId },
          replace: true,
        })
      }
    }

    const preloads = [
      context.queryClient.ensureQueryData(
        categoryQueries.index({ organizationId: params.orgId, filters: { sort: 'pos' } })
      ),
      ensurePosSetupRequirements(context.queryClient, {
        organizationId: params.orgId,
        locationFilterParams: posSetupLocationFilters,
      }),
    ]

    if (deps?.cartId) {
      preloads.push(
        Promise.resolve(
          context.queryClient.ensureQueryData(
            cartQueries.posShow({
              organizationId: params.orgId,
              cartId: deps.cartId,
            })
          )
        )
          .then((cart) => {
            if (!preload) rememberLastPosCart(params.orgId, cart)
          })
          .catch(() => null)
      )
    }

    await Promise.all(preloads)

    return { isReadOnly: false }
  },
})

export function CreateSalesOrder() {
  const navigate = useNavigate()
  const { cartId, duplicateReview } = Route.useSearch()
  const { orgId } = useParams({ strict: false })

  const { locations, isReady, showLocationsCard, showStripeCard } = usePosSetupRequirements({
    organizationId: orgId,
    locationFilterParams: posSetupLocationFilters,
  })

  const previousOrgId = usePrevious(orgId)

  const createCartMutation = useCreateCartMutation()
  const isCartMutating = useIsCartMutating()
  const isCartOrderEnding = useIsCartOrderEnding()
  // Catalog adds queue here, so this page's cart and its unsaved adds outlive catalog renders.
  const [cartActions] = useState(createPosCartActions)
  const {
    data: cart,
    isLoading,
    error,
  } = usePosCart(cartId, {
    organizationId: orgId,
    enabled: !!cartId,
  })
  const defaultLocation = locations.find((loc) => loc.is_default) || locations[0] || null
  const [salesOrderTransitionCart, setSalesOrderTransitionCart] = useState(null)
  const [isPaymentWindowAskingOnLeave, setIsPaymentWindowAskingOnLeave] = useState(false)
  const cartWithPendingAdds = usePosCartWithPendingAdds(cartActions, cart ?? null)
  const displayCart = salesOrderTransitionCart ?? cartWithPendingAdds
  // An unsaved cart holding a payment is only findable again through the recovery banner, so
  // leaving it (sidebar, Back, closing the tab) asks first. Saving, Start New Cart and the POS's
  // own redirects pass ignoreBlocker; a cart being completed is not held here.
  const mustConfirmLeavingCart =
    !salesOrderTransitionCart && getPosCartLeaveAction(cart) === 'save-first'
  useBlocker({
    disabled: !mustConfirmLeavingCart,
    shouldBlockFn: (navigation) =>
      shouldAskBeforeLeavingCart(navigation, {
        cartId: cart?.id,
        paymentWindowAsking: isPaymentWindowAskingOnLeave,
      }) && !confirm(UNSAVED_PAID_CART_LEAVE_MESSAGE),
    enableBeforeUnload: mustConfirmLeavingCart,
  })
  const cartLocationId = Number(displayCart?.location_id ?? displayCart?.location?.id ?? 0)
  const defaultLocationId = Number(defaultLocation?.id ?? 0)
  const currentCartId = displayCart?.id ?? null
  const [isPosPaymentModalActive, setIsPosPaymentModalActive] = useState(false)
  const [isPosSalesOrderNavigationPending, setIsPosSalesOrderNavigationPending] = useState(false)
  const [locationSelection, setLocationSelection] = useState(() => ({
    orgId,
    cartId: currentCartId,
    locationId: null,
  }))
  const isPosLockBannerSuppressed = isPosPaymentModalActive || isPosSalesOrderNavigationPending

  const captureSalesOrderTransitionCart = (cartSnapshot = displayCart) => {
    setSalesOrderTransitionCart(
      (currentSnapshot) => currentSnapshot ?? cloneCartSnapshot(cartSnapshot)
    )
  }

  const releaseSalesOrderTransitionCart = () => {
    setSalesOrderTransitionCart(null)
  }

  const clearCartId = () => {
    forgetLastPosCart(orgId, currentCartId)
    cartActions.leaveCart()
    releaseSalesOrderTransitionCart()
    setIsPosSalesOrderNavigationPending(false)
    void navigate({
      search: (prev) => ({ ...prev, cartId: undefined, duplicateReview: undefined }),
      ignoreBlocker: true,
      replace: true,
    })
  }

  const clearDuplicateReview = () => {
    void navigate({
      search: (prev) => ({ ...prev, duplicateReview: undefined }),
      ignoreBlocker: true,
      replace: true,
    })
  }

  const clearCartIdFromEffect = useEffectEvent(() => {
    clearCartId()
  })

  useEffect(() => {
    if (previousOrgId === null || previousOrgId === orgId) return
    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    clearCartIdFromEffect()
  }, [orgId, previousOrgId])

  useEffect(() => {
    if (error) {
      toast.error('Failed to load cart, creating new one...', {
        description: getErrorMessage(error),
      })
      // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
      clearCartIdFromEffect()
    }
  }, [error])

  if (isLoading) return <DefaultPending />

  if (!isReady) {
    return (
      <div className="space-y-4">
        <CreateSalesOrderPageHeader />
        <SetupRequirementsCards
          showLocationsCard={showLocationsCard}
          showStripeCard={showStripeCard}
          locationsDescription="You need to add at least one location before you can create orders."
          stripeDescription="You need to configure Stripe Connect before you can create orders. Set it up from Stripe in Settings."
        />
      </div>
    )
  }

  const scopedSelectedLocationId =
    locationSelection.orgId === orgId && locationSelection.cartId === currentCartId
      ? Number(locationSelection.locationId ?? 0)
      : 0
  const selectedLocationId =
    scopedSelectedLocationId &&
    locations.some((location) => Number(location?.id ?? 0) === scopedSelectedLocationId)
      ? scopedSelectedLocationId
      : cartLocationId || defaultLocationId || null
  const selectedLocationFromList =
    locations.find((location) => Number(location?.id ?? 0) === Number(selectedLocationId ?? 0)) ??
    null
  const resolvedSelectedLocation = resolvePosFulfillmentLocation(displayCart, {
    selectedLocation: selectedLocationFromList,
    locations,
    defaultLocation,
  })
  // Controls that need a cart share one, however many ask before the page shows it.
  const createCartWithDefaults = (location = resolvedSelectedLocation || defaultLocation) =>
    cartActions.ensureCart(orgId, async () => {
      const locationToUse = location || resolvedSelectedLocation || defaultLocation
      if (!locationToUse) {
        throw new Error('No default location available')
      }

      const newCart = await createCartMutation.mutateAsync({
        location_id: locationToUse.id,
        fulfillment_type: FulfillmentType.InStore.Value,
        source: CartSource.POS.Value,
        organization_workstation_id: loadLocalPrintProfile(orgId).workstationId || null,
      })
      // Load the cart before showing it, so the page never shows it half-loaded and a change saved
      // right away can't race its first load. If the load fails, the page's own load reports it.
      await queryClient
        .ensureQueryData(cartQueries.posShow({ organizationId: orgId, cartId: newCart.id }))
        .catch(() => null)

      void navigate({
        search: (prev) => ({ ...prev, cartId: newCart.id, duplicateReview: undefined }),
        replace: true,
      })

      return newCart
    })

  const handleSalesOrderNavigationStart = () => {
    captureSalesOrderTransitionCart()
    setIsPosSalesOrderNavigationPending(true)
  }

  const handleSalesOrderNavigationEnd = () => {
    setIsPosSalesOrderNavigationPending(false)
    releaseSalesOrderTransitionCart()
  }

  const handleLocationChange = (locationId) => {
    setLocationSelection({
      orgId,
      cartId: currentCartId,
      locationId,
    })
  }

  const header = (
    <CreateSalesOrderPageHeader
      actions={
        <>
          <LocationSelector
            locations={locations}
            cart={displayCart}
            defaultLocation={defaultLocation}
            selectedLocationId={selectedLocationId}
            onLocationChange={handleLocationChange}
            disabled={isCartMutating > 0}
            createCartWithDefaults={createCartWithDefaults}
          />
          {!displayCart?.id ? <SavedPosCartsLink orgId={orgId} /> : null}
          <CashDrawerMenu />
        </>
      }
    />
  )
  const banners = (
    <>
      <div className="shrink-0">
        <RecoverablePosCartBanner currentCartId={displayCart?.id ?? cartId} orgId={orgId} />
      </div>
      <div className="shrink-0">
        {!isPosLockBannerSuppressed ? (
          <PosCartLockBanner cart={displayCart} onStartNewCart={clearCartId} />
        ) : null}
      </div>
    </>
  )
  const catalog = (
    <ProductCatalog
      cart={displayCart}
      cartId={cartId ?? null}
      cartActions={cartActions}
      createCartWithDefaults={createCartWithDefaults}
      disabled={isCartOrderEnding}
      className="lg:h-full lg:min-h-0"
    />
  )
  const cartPanel = (
    <POSCart
      cart={displayCart}
      location={resolvedSelectedLocation}
      createCartWithDefaults={createCartWithDefaults}
      defaultLocation={defaultLocation}
      onLockBannerSuppressionChange={setIsPosPaymentModalActive}
      onPaymentNavigationBlockingChange={setIsPaymentWindowAskingOnLeave}
      onSalesOrderFinalizeStart={captureSalesOrderTransitionCart}
      onSalesOrderFinalizeEnd={releaseSalesOrderTransitionCart}
      onSalesOrderNavigationStart={handleSalesOrderNavigationStart}
      onSalesOrderNavigationEnd={handleSalesOrderNavigationEnd}
      onStartNewOrder={clearCartId}
      duplicateReviewMode={duplicateReview}
      onDuplicateReviewOpened={clearDuplicateReview}
      className="lg:h-full lg:min-h-0"
    />
  )

  return <PosWorkspaceLayout header={header} banners={banners} catalog={catalog} cart={cartPanel} />
}
